import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Space,
  Typography,
  App,
  Alert,
  Tag,
  Modal,
  Input,
} from 'antd';
import { CopyOutlined, QrcodeOutlined, LinkOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import spotPurchaseService from '@/services/spotPurchase.service';
import { useAuthStore } from '@/store/authStore';
import { buildUserIdToLabelMap, formatUserRecord, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import type { User } from '@/services/user.service';

const { Text, Paragraph } = Typography;

interface Props {
  prId: string;
  prStatus: string;
}

function purchaserLabel(
  row: { purchaser_user_id?: string; purchaser_name?: string; purchaser_email?: string },
  userMap: Map<string, string>
): string {
  if (row.purchaser_name) return row.purchaser_name;
  if (row.purchaser_email) return row.purchaser_email;
  return resolveUserDisplayFromMap(userMap, row.purchaser_user_id);
}

export const SpotPurchasePanel: React.FC<Props> = ({ prId, prStatus }) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const canAssign =
    permissions.includes('procurement.spot_purchase.assign') ||
    permissions.includes('procurement.spot_purchase.supervise') ||
    permissions.includes('procurement.view');
  const canSupervise =
    permissions.includes('procurement.spot_purchase.supervise') ||
    permissions.includes('procurement.spot_purchase.assign');
  const canReserve =
    permissions.includes('procurement.spot_purchase.reserve') ||
    permissions.includes('procurement.spot_purchase.submit');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [shareSupplierId, setShareSupplierId] = useState<string | undefined>();
  const [shareLineIds, setShareLineIds] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [qrModal, setQrModal] = useState<{ url: string; title: string } | null>(null);

  const approved = ['Approved', 'Partially Ordered'].includes(prStatus);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['spot-purchase', 'assignments', prId] });
    queryClient.invalidateQueries({ queryKey: ['spot-purchase', 'availability', prId] });
    queryClient.invalidateQueries({ queryKey: ['spot-purchase', 'reservations', prId] });
    queryClient.invalidateQueries({ queryKey: ['spot-purchase', 'shares', prId] });
  };

  const { data: assignments = [], isLoading: loadingAssign } = useQuery({
    queryKey: ['spot-purchase', 'assignments', prId],
    queryFn: () => spotPurchaseService.listAssignments(prId),
    enabled: approved && !!prId,
  });

  const { data: availability, isLoading: loadingAvail } = useQuery({
    queryKey: ['spot-purchase', 'availability', prId],
    queryFn: () => spotPurchaseService.getAvailability(prId),
    enabled: approved && !!prId,
    refetchInterval: 30_000,
  });

  const { data: reservations = [], isLoading: loadingRes } = useQuery({
    queryKey: ['spot-purchase', 'reservations', prId],
    queryFn: () => spotPurchaseService.listReservations(prId),
    enabled: approved && !!prId,
    refetchInterval: 30_000,
  });

  const { data: shares = [], isLoading: loadingShares } = useQuery({
    queryKey: ['spot-purchase', 'shares', prId],
    queryFn: () => spotPurchaseService.listSupplierShares(prId),
    enabled: approved && !!prId && canReserve,
    refetchInterval: 30_000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users', 'spot-assign'],
    queryFn: async () => {
      const rows = await spotPurchaseService.listEligiblePurchasers();
      return (Array.isArray(rows) ? rows : []) as User[];
    },
    enabled: approved && canAssign,
    staleTime: 60_000,
  });

  const userMap = useMemo(() => buildUserIdToLabelMap(users as User[]), [users]);

  const userOptions = useMemo(
    () =>
      (users as User[]).map((u) => ({
        value: u.id,
        label: formatUserRecord(u) || u.username || u.email || u.id,
      })),
    [users]
  );

  const lines = ((availability as any)?.lines ?? []) as any[];
  const lineLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of lines) {
      const code = row.item_code ? `${row.item_code} — ` : '';
      m.set(row.pr_line_id, `#${row.line_number} ${code}${row.item_description || ''}`.trim());
    }
    return m;
  }, [lines]);

  const myReservedLineOptions = useMemo(() => {
    const byLine = new Map<string, number>();
    for (const r of reservations as any[]) {
      if (String(r.status || 'ACTIVE').toUpperCase() !== 'ACTIVE') continue;
      if (currentUserId && String(r.purchaser_user_id || '') !== String(currentUserId)) continue;
      const lid = String(r.pr_line_id || '');
      if (!lid) continue;
      byLine.set(lid, (byLine.get(lid) || 0) + (Number(r.qty) || 0));
    }
    return [...byLine.entries()].map(([value, qty]) => ({
      value,
      label: `${lineLabelById.get(value) || value} (held ${qty})`,
    }));
  }, [reservations, lineLabelById, currentUserId]);

  const activeShares = useMemo(
    () => (Array.isArray(shares) ? shares : []).filter((s: any) => s.status === 'ACTIVE'),
    [shares]
  );

  const searchSuppliers = async (q: string) => {
    const rows = await spotPurchaseService.searchSuppliers(q, 30);
    setSupplierOptions(
      (rows as any[]).map((s) => ({
        value: s.id,
        label: `${s.supplier_name || ''}${s.supplier_code ? ` (${s.supplier_code})` : ''}`,
      }))
    );
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('Link copied — paste to WhatsApp / email');
    } catch {
      message.error('Could not copy link');
    }
  };

  const assignMutation = useMutation({
    mutationFn: () => spotPurchaseService.assign(prId, selectedUsers),
    onSuccess: () => {
      message.success('Purchasers assigned for market purchase');
      setSelectedUsers([]);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Assign failed'),
  });

  const unassignMutation = useMutation({
    mutationFn: (uid: string) => spotPurchaseService.unassign(prId, uid),
    onSuccess: () => {
      message.success('Purchaser unassigned');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Unassign failed'),
  });

  const releaseMutation = useMutation({
    mutationFn: (reservationId: string) =>
      spotPurchaseService.releaseReservation(reservationId, true, 'Supervisor release'),
    onSuccess: () => {
      message.success('Reservation released');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Release failed'),
  });

  const createShareMutation = useMutation({
    mutationFn: () =>
      spotPurchaseService.createSupplierShare(prId, {
        supplier_id: shareSupplierId!,
        pr_line_ids: shareLineIds,
      }),
    onSuccess: (data: any) => {
      message.success('Supplier link created');
      const url = data?.portal_url as string | undefined;
      if (url) {
        setQrModal({ url, title: 'Share with supplier' });
      }
      setShareLineIds([]);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not create share link'),
  });

  const revokeShareMutation = useMutation({
    mutationFn: (shareId: string) => spotPurchaseService.revokeSupplierShare(shareId),
    onSuccess: () => {
      message.success('Share link revoked');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Revoke failed'),
  });

  if (!approved) {
    return (
      <Card title="Spot / Market Purchase" size="small" style={{ marginTop: 16 }}>
        <Text type="secondary">Available after the Purchase Request is approved.</Text>
      </Card>
    );
  }

  return (
    <Card title="Spot / Market Purchase" size="small" style={{ marginTop: 16 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Self-assign or optional supervisor assign"
        description="Purchasers can pick this PR from Spot / Market Purchase (or mobile). Reserving or saving an offer also self-assigns. Critical lines stay on Inquiry → Quotation → Bid Analysis."
      />

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Live quantity board
      </Text>
      <Table
        size="small"
        rowKey="pr_line_id"
        loading={loadingAvail}
        pagination={false}
        style={{ marginBottom: 16 }}
        dataSource={lines}
        columns={[
          { title: '#', dataIndex: 'line_number', width: 48 },
          {
            title: 'Item',
            render: (_: unknown, row: any) => (
              <span>
                {row.item_code ? `${row.item_code} — ` : ''}
                {row.item_description}
                {row.is_critical ? <Tag color="red" style={{ marginLeft: 8 }}>Critical</Tag> : null}
                {!row.is_critical && row.eligible ? (
                  <Tag color="teal" style={{ marginLeft: 8 }}>
                    Eligible
                  </Tag>
                ) : null}
              </span>
            ),
          },
          { title: 'Requested', dataIndex: 'requested_quantity', width: 90 },
          { title: 'PO committed', dataIndex: 'ordered_quantity', width: 100 },
          { title: 'Reserved (me)', dataIndex: 'reserved_by_me', width: 100 },
          { title: 'Reserved (others)', dataIndex: 'reserved_by_others', width: 120 },
          { title: 'Available', dataIndex: 'available_quantity', width: 90 },
          {
            title: 'Last rate (advisory)',
            dataIndex: 'last_purchase_rate',
            width: 130,
            render: (v: number | null) => (v != null ? Number(v).toLocaleString() : '—'),
          },
        ]}
        locale={{ emptyText: 'No PR lines' }}
      />

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Active reservations
      </Text>
      <Table
        size="small"
        rowKey="id"
        loading={loadingRes}
        pagination={false}
        style={{ marginBottom: 16 }}
        dataSource={reservations as any[]}
        columns={[
          {
            title: 'Purchaser',
            key: 'purchaser',
            render: (_: unknown, row: any) => purchaserLabel(row, userMap),
          },
          {
            title: 'Line',
            key: 'line',
            ellipsis: true,
            render: (_: unknown, row: any) =>
              row.line_label || lineLabelById.get(row.pr_line_id) || '—',
          },
          { title: 'Qty', dataIndex: 'qty', width: 80 },
          {
            title: 'Expires',
            dataIndex: 'expires_at',
            render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
          },
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, row: any) =>
              canSupervise ? (
                <Button
                  type="link"
                  danger
                  size="small"
                  loading={releaseMutation.isPending}
                  onClick={() => releaseMutation.mutate(row.id)}
                >
                  Release
                </Button>
              ) : null,
          },
        ]}
        locale={{ emptyText: 'No active reservations' }}
      />

      {canReserve && (
        <>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Share with supplier (link / QR)
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Create a link for one approved supplier. They open it on a laptop to enter qty, price, and
            attachments. You still submit the PO.
          </Paragraph>
          <Space wrap style={{ marginBottom: 12 }}>
            <Select
              showSearch
              allowClear
              style={{ minWidth: 260 }}
              placeholder="Select supplier"
              filterOption={false}
              onSearch={(q) => {
                void searchSuppliers(q);
              }}
              onFocus={() => {
                if (!supplierOptions.length) void searchSuppliers('');
              }}
              options={supplierOptions}
              value={shareSupplierId}
              onChange={setShareSupplierId}
            />
            <Select
              mode="multiple"
              style={{ minWidth: 320 }}
              placeholder="Reserved lines to share"
              options={myReservedLineOptions}
              value={shareLineIds}
              onChange={setShareLineIds}
              optionFilterProp="label"
            />
            <Button
              type="primary"
              icon={<LinkOutlined />}
              disabled={!shareSupplierId || !shareLineIds.length}
              loading={createShareMutation.isPending}
              onClick={() => createShareMutation.mutate()}
            >
              Create link &amp; QR
            </Button>
          </Space>
          <Table
            size="small"
            rowKey="id"
            loading={loadingShares}
            pagination={false}
            style={{ marginBottom: 16 }}
            dataSource={activeShares as any[]}
            columns={[
              {
                title: 'Supplier',
                key: 'supplier',
                render: (_: unknown, row: any) =>
                  row.supplier_name || row.supplier_id || '—',
              },
              {
                title: 'Link',
                dataIndex: 'portal_url',
                ellipsis: true,
                render: (url: string) =>
                  url ? <Input value={url} readOnly size="small" /> : '—',
              },
              {
                title: 'Expires',
                dataIndex: 'expires_at',
                width: 160,
                render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 280,
                render: (_: unknown, row: any) => {
                  const url = String(row.portal_url || '');
                  return (
                    <Space wrap size="small">
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        disabled={!url}
                        onClick={() => copyLink(url)}
                      >
                        Copy link
                      </Button>
                      <Button
                        size="small"
                        icon={<QrcodeOutlined />}
                        disabled={!url}
                        onClick={() =>
                          setQrModal({
                            url,
                            title: row.supplier_name || 'Supplier QR',
                          })
                        }
                      >
                        Show QR
                      </Button>
                      <Button
                        size="small"
                        danger
                        loading={revokeShareMutation.isPending}
                        onClick={() => revokeShareMutation.mutate(row.id)}
                      >
                        Revoke
                      </Button>
                    </Space>
                  );
                },
              },
            ]}
            locale={{ emptyText: 'No active supplier links yet' }}
          />
        </>
      )}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Purchasers
      </Text>
      <Table
        size="small"
        rowKey="id"
        loading={loadingAssign}
        pagination={false}
        dataSource={assignments as any[]}
        columns={[
          {
            title: 'Purchaser',
            key: 'purchaser',
            render: (_: unknown, row: any) => purchaserLabel(row, userMap),
          },
          {
            title: 'Assigned',
            dataIndex: 'assigned_at',
            render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
          },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, row: any) =>
              canAssign ? (
                <Button
                  type="link"
                  danger
                  size="small"
                  loading={unassignMutation.isPending}
                  onClick={() => unassignMutation.mutate(row.purchaser_user_id)}
                >
                  Unassign
                </Button>
              ) : null,
          },
        ]}
        locale={{ emptyText: 'No purchasers assigned yet (self-claim still allowed)' }}
      />
      {canAssign && (
        <Space style={{ marginTop: 12 }} wrap>
          <Select
            mode="multiple"
            style={{ minWidth: 280 }}
            placeholder="Select purchasers (optional)"
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
            optionFilterProp="label"
            showSearch
          />
          <Button
            type="primary"
            disabled={!selectedUsers.length}
            loading={assignMutation.isPending}
            onClick={() => assignMutation.mutate()}
          >
            Assign
          </Button>
          <Button type="link" onClick={() => navigate('/scm/procurement/spot-purchase')}>
            Open board
          </Button>
        </Space>
      )}

      <Modal
        open={Boolean(qrModal)}
        title={qrModal?.title || 'Supplier QR'}
        onCancel={() => setQrModal(null)}
        footer={[
          <Button key="close" onClick={() => setQrModal(null)}>
            Close
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => qrModal?.url && copyLink(qrModal.url)}
          >
            Copy link
          </Button>,
        ]}
      >
        {qrModal?.url ? (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-block',
                padding: 16,
                background: '#fff',
                borderRadius: 8,
              }}
            >
              <QRCodeSVG value={qrModal.url} size={220} includeMargin />
            </div>
            <Paragraph type="secondary" style={{ marginTop: 12 }}>
              Show this QR to the supplier, or copy the link to send via WhatsApp / email.
            </Paragraph>
            <Input.TextArea value={qrModal.url} readOnly autoSize={{ minRows: 2, maxRows: 4 }} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
};

export default SpotPurchasePanel;
